import PropTypes from "prop-types";
import { useEffect } from "react";

import { Controller, useFormContext } from "react-hook-form";

import { Paper } from "@mui/material";
import Grid from "@mui/material/Grid";

import {
  AllOutletsSelect,
  OutletRoleEnum,
  UserOutletsSelect,
} from "@/features/outlets";
import { SellerBankAccountSelect } from "@/features/seller-bank-accounts";
import { SupplierSelect } from "@/features/suppliers";
import { CustomDatePicker, CustomInputField } from "@/features/ui";

export const GeneralInformationSection = ({ editMode = false }) => {
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const seller = watch("seller");
  const buyer = watch("buyer");
  const sellerableType = watch("sellerableType");
  const purchaseInvoiceProductVariants = watch(
    "purchaseInvoiceProductVariants",
  );
  const purchaseInvoiceExpenses = watch("purchaseInvoiceExpenses");

  // Disable buyer and seller when there are existing products or expenses
  const disableBuyerSeller =
    purchaseInvoiceProductVariants.length > 0 ||
    purchaseInvoiceExpenses.length > 0;

  // Auto-set sellerableType based on buyer's role
  useEffect(() => {
    if (editMode) return;

    if (buyer) {
      const sellerableType =
        buyer.role === OutletRoleEnum.HUB ? "SUPPLIER" : "OUTLET";
      setValue("sellerableType", sellerableType);
    }
  }, [buyer, editMode, setValue]);

  useEffect(() => {
    if (editMode) return;

    if (sellerableType) {
      setValue("seller", null);
      setValue("sellerBankAccount", null);
    }
  }, [sellerableType, editMode, setValue]);

  useEffect(() => {
    if (seller?.supplierTaxInformation) {
      setValue("taxable", seller.supplierTaxInformation.defaultTaxIncluded);
    }
    if (seller?.defaultPaymentTerm) {
      setValue("paymentTerm", seller.defaultPaymentTerm);
    }
  }, [seller, setValue]);

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, lg: 5 }}>
          <Controller
            control={control}
            name="seller"
            render={({ field, fieldState }) =>
              sellerableType === "OUTLET" ? (
                <AllOutletsSelect
                  value={field.value}
                  onChange={(val) => {
                    field.onChange(val);
                    setValue("sellerBankAccount", null);
                  }}
                  allowedRoles={[OutletRoleEnum.HUB]}
                  disabled={editMode || disableBuyerSeller}
                  TextFieldProps={{
                    label: "Pemasok (Hub)",
                    required: true,
                    error: fieldState.invalid,
                    helperText: fieldState.error?.message,
                  }}
                />
              ) : (
                <SupplierSelect
                  withSupplierTaxInformation={true}
                  withPaymentTerm={true}
                  value={field.value}
                  onChange={(val) => {
                    field.onChange(val);
                    setValue("sellerBankAccount", null);
                  }}
                  disabled={editMode || disableBuyerSeller}
                  TextFieldProps={{
                    label: "Pemasok (Eksternal)",
                    required: true,
                    error: fieldState.invalid,
                    helperText: fieldState.error?.message,
                  }}
                />
              )
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            control={control}
            name="buyer"
            render={({ field, fieldState }) => (
              <UserOutletsSelect
                value={field.value}
                onChange={field.onChange}
                disabled={editMode || disableBuyerSeller}
                TextFieldProps={{
                  label: "Outlet",
                  required: true,
                  error: fieldState.invalid,
                  helperText: fieldState.error?.message,
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Controller
            name="transactionDate"
            control={control}
            render={({ field, fieldState }) => (
              <CustomDatePicker
                value={field.value}
                onChange={field.onChange}
                required
                label="Tanggal Transaksi"
                error={fieldState.error}
                helperText={fieldState.error?.message}
                InputProps={{
                  disableFuture: true,
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <CustomInputField
            required
            name="sellerReferenceNumber"
            label="No. Faktur Penjual"
            register={register}
            helperText={errors.sellerReferenceNumber?.message}
            error={Boolean(errors.sellerReferenceNumber)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            name="sellerBankAccount"
            control={control}
            render={({ field, fieldState }) => (
              <SellerBankAccountSelect
                sellerableId={seller?.id}
                sellerableType={sellerableType}
                value={field.value}
                onChange={field.onChange}
                disabled={!seller}
                TextFieldProps={{
                  label: "Rekening Bank Pemasok",
                  placeholder: "Pilih Rekening",
                  error: fieldState.invalid,
                  helperText: fieldState.error?.message,
                }}
              />
            )}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

GeneralInformationSection.propTypes = {
  editMode: PropTypes.bool,
};
